type TurnstileRenderOptions = {
  sitekey: string;
  size?: 'normal' | 'compact' | 'flexible';
  execution?: 'render' | 'execute';
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
};

type TurnstileAPI = {
  render: (container: string | HTMLElement, options: TurnstileRenderOptions) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

let loadPromise: Promise<TurnstileAPI> | null = null;

function loadTurnstileScript(): Promise<TurnstileAPI> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<TurnstileAPI>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-ch-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.turnstile) resolve(window.turnstile);
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile')));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-ch-turnstile', 'true');
    script.onload = () => {
      if (!window.turnstile) {
        reject(new Error('Turnstile API unavailable after load'));
        return;
      }
      resolve(window.turnstile);
    };
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Build the CSS for the Turnstile host container based on widget position.
 * The container is positioned with `position: fixed` so it visually overlaps
 * the widget chat panel's input area. The compact Turnstile widget is 130x120px.
 *
 * When hidden (no active challenge) the container is moved offscreen so it
 * doesn't intercept clicks but remains renderable (Turnstile needs a non-
 * display:none container to inject its iframe).
 */
function containerStyles(position: string, visible: boolean): string {
  if (!visible) {
    return 'position:fixed;bottom:-9999px;left:-9999px;z-index:2147483646;pointer-events:none;';
  }

  const base = [
    'position:fixed',
    'z-index:2147483647',
    'pointer-events:auto',
    'transition:opacity 0.2s ease',
  ];

  if (position === 'bottom-left') {
    base.push('bottom:70px', 'left:24px');
  } else {
    base.push('bottom:70px', 'right:24px');
  }

  return base.join(';') + ';';
}

export type TurnstileExecutor = () => Promise<string>;

export async function createTurnstileExecutor(
  siteKey: string,
  position: string = 'bottom-right',
): Promise<TurnstileExecutor> {
  const turnstile = await loadTurnstileScript();

  // Turnstile cannot render inside Shadow DOM — it traverses the DOM tree and
  // crashes on shadow boundaries. Create a container on document.body so the
  // iframe renders in the regular DOM, positioned with `position: fixed` so it
  // visually appears near the widget chat panel.
  let container = document.getElementById('ch-turnstile-host');
  if (!container) {
    container = document.createElement('div');
    container.id = 'ch-turnstile-host';
    document.body.appendChild(container);
  }
  container.style.cssText = containerStyles(position, false);

  let resolveToken: ((token: string) => void) | null = null;
  let rejectToken: ((err: Error) => void) | null = null;
  let cachedToken: string | null = null;

  function showContainer(): void {
    if (container) container.style.cssText = containerStyles(position, true);
  }

  function hideContainer(): void {
    if (container) container.style.cssText = containerStyles(position, false);
  }

  const widgetId = turnstile.render(container, {
    sitekey: siteKey,
    size: 'compact',
    callback: (token: string) => {
      hideContainer();
      if (resolveToken) {
        resolveToken(token);
        resolveToken = null;
        rejectToken = null;
      } else {
        cachedToken = token;
      }
    },
    'error-callback': () => {
      hideContainer();
      if (rejectToken) rejectToken(new Error('Turnstile challenge failed'));
      resolveToken = null;
      rejectToken = null;
    },
    'expired-callback': () => {
      hideContainer();
      cachedToken = null;
      if (rejectToken) rejectToken(new Error('Turnstile token expired'));
      resolveToken = null;
      rejectToken = null;
    },
  });

  return () => {
    if (cachedToken) {
      const token = cachedToken;
      cachedToken = null;
      return Promise.resolve(token);
    }

    return new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        hideContainer();
        resolveToken = null;
        rejectToken = null;
        reject(new Error('Turnstile challenge timed out'));
      }, 120_000);

      const showDelay = setTimeout(() => showContainer(), 1000);

      resolveToken = (token: string) => {
        clearTimeout(showDelay);
        clearTimeout(timeout);
        hideContainer();
        resolve(token);
      };
      rejectToken = (err: Error) => {
        clearTimeout(showDelay);
        clearTimeout(timeout);
        hideContainer();
        reject(err);
      };

      turnstile.reset(widgetId);
    });
  };
}
